import React, { useState, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Modal } from '@components/index';
import { getPetSelector } from '@modules/subscriptions/core/selectors';
import { isPastAdultGracePeriod } from '@modules/subscriptions/utils/getEffectiveDogStage';
import { PetProfileUpdateModal } from '../pet-profile-update/PetProfileUpdateModal';

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const DAYS_BEFORE_ADULT = 14;

export const PuppyToAdultTransition: React.FC = () => {
  const intl = useIntl();
  const pet = useSelector(getPetSelector);
  const [dismissed, setDismissed] = useState(false);
  const [showWeightModal, setShowWeightModal] = useState(false);

  const storageKey = useMemo(
    () => (pet?.id ? `puppy_adult_transition_${pet.id}` : null),
    [pet?.id],
  );

  const shouldShow = useMemo(() => {
    if (!pet) return false;
    if (pet.dog_stage !== 'puppy') return false;
    if (!pet.adult_when) return false;
    if (isPastAdultGracePeriod(pet)) return false;

    const daysUntilAdult = (new Date(pet.adult_when).getTime() - Date.now()) / MS_PER_DAY;
    return daysUntilAdult <= DAYS_BEFORE_ADULT;
  }, [pet]);

  // One-time only: if already dismissed (localStorage key exists), never show again
  const alreadyDismissed = useMemo(() => {
    if (!storageKey) return false;
    try {
      return !!localStorage.getItem(storageKey);
    } catch {
      return false;
    }
  }, [storageKey]);

  const isIntroOpen = shouldShow && !dismissed && !alreadyDismissed && !showWeightModal;

  const handleDismiss = () => {
    setDismissed(true);
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, new Date().toISOString());
      } catch {
        // localStorage unavailable
      }
    }
    // Notify the global banner to show
    window.dispatchEvent(new CustomEvent('puppy-transition-dismissed'));
  };

  const handleConfirm = () => {
    setShowWeightModal(true);
  };

  const handleWeightModalClose = () => {
    setShowWeightModal(false);
    handleDismiss();
  };

  if (!shouldShow || alreadyDismissed) return null;

  return (
    <>
      {/* Intro announcement modal — shown once */}
      <Modal isOpen={isIntroOpen} handleClose={handleDismiss}>
        <div className="growth-phase-reminder-modal">
          <div className="growth-phase-reminder-modal__icon">
            <span className="growth-phase-reminder-modal__emoji">🐕</span>
          </div>
          <h3 className="growth-phase-reminder-modal__title">
            <FormattedMessage
              id="SUBSCRIPTION.TRANSITION.MODAL_TITLE"
              values={{ name: pet?.name || intl.formatMessage({ id: 'GLOBAL.DOG_FALLBACK' }) }}
            />
          </h3>
          <p className="growth-phase-reminder-modal__text">
            <FormattedMessage id="SUBSCRIPTION.TRANSITION.MODAL_TEXT_1" />
            <br />
            <br />
            <FormattedMessage id="SUBSCRIPTION.TRANSITION.MODAL_TEXT_2" />
          </p>
          <div className="growth-phase-reminder-modal__actions">
            <button type="button" className="recipe-btn active" onClick={handleConfirm}>
              <span>
                <FormattedMessage id="SUBSCRIPTION.TRANSITION.CONFIRM_CTA" />
              </span>
            </button>
          </div>
        </div>
      </Modal>

      {/* Weight confirmation modal */}
      <PetProfileUpdateModal
        isOpen={showWeightModal}
        onClose={handleWeightModalClose}
        mode="transition"
      />
    </>
  );
};
